/**
 * 74.1 (DECISIONS 337): the system in the sky — the sun as a disc with glare, every other body at its true direction and
 * (dialled) angular size, lost in daylight by brightness and behind cloud by depth. Presentation only: it reads a
 * `SkyFromBody` (core/systemGen.ts `skyFrom`, pure) and draws; nothing here reaches the Match.
 *
 * **The trick** is orbitSky's (67.75): a body is drawn at a fixed `distance` from the eye along its direction, scaled so its
 * angle is right — inside the camera's far plane, beyond anything on the ground, so the ground and the cloud layer in
 * front of it occlude it as any far thing (depth test on, depth write off).
 *
 * **One draw for every body**: an instanced quad whose fragment shader shades a sphere — the normal from the quad's
 * coordinates, lit by the sun's direction *from that body* (so the phases are right: a crescent neighbour, a half-lit
 * giant) — Lambert over the lit face from a soft terminator, the limb darkened, so a large body reads as a sphere and not a
 * cutout (347) — cloud bands on a gas giant, an air world's bright rim, a ring as a tilted ellipse drawn over the disc on
 * its near side and hidden behind it on the far, the ring's shadow on the face and the body's on the ring (`RING_MATH`
 * is the same math in TypeScript, for the tests). Under ~1.5 px of radius the quad holds a minimum size
 * and draws a soft dot instead (a bright star's look). **The sun** is one more quad, additive: a disc in the star's core
 * colour and a glare in its corona's, broad by day in air, tight on an airless world.
 *
 * **Lost in daylight** (`visibility`): a lit face's surface brightness is albedo × the sun's flux at the body; by day the
 * sky's brightness competes with it (a thin air leaves a darker day sky; an airless world's sky is black at noon). A
 * resolved disc shows when its brightness beats the sky's by a margin (the day Moon's faint case); a dot's whole flux
 * (brightness × its true angular area) has to — Venus by day, Jupiter at night, Mars not by day. Bodies in the sun's glare
 * go, so does a body on its night side (a new moon), and a body is hazed toward the sky's colour the lower it stands.
 *
 * **Low-detail spheres** (`spheres = true`): a body over `SPHERE_PX` of diameter is drawn as a pooled icosphere (320
 * triangles, one draw) in place of its impostor — the comparison the sheet shows. It carries its own light: the scene's sun
 * light is the *viewer's*, dimmed by the viewer's dusk, while a body in the sky stands in full sun (the ISS's hour), so the
 * sphere's material shades by the same sun-at-the-body direction as the impostor. Cost: 2 draws + 2 triangles a body; a
 * sphere +1 draw and 320 triangles each; the silhouette shows its facets at 25° across, where the impostor's disc is round.
 *
 * **The big bodies' surface** (74.6, DECISIONS 348; `BigBodyLook`): the owner saw 74.1's giant bands as "corduroy" — a
 * regular stripe, and screen-aligned. The sheet's looks draw the surface on the sphere by the body's own pole, compiled in
 * (`BIG_LOOK`); the game draws `DEFAULT_BIG_BODY_LOOK` — C painted since DECISIONS 349.
 */
import * as THREE from 'three';
import type { SkyFromBody, SkyView, SystemBody } from '../core/systemGen';
import type { PlanetArchetype, Vec3 } from '../types';
import { NIGHT_FLOOR, skyBrightness } from '../core/starField';

/** A body's look in the sky: its colour and albedo; air worlds get a rim. */
export const SKY_BODY_LOOKS: Record<PlanetArchetype | 'gasGiant' | 'iceGiant', { color: string; albedo: number; air: boolean }> = {
  thalia: { color: '#5f8fc8', albedo: 0.32, air: true },    // blue-green with white cloud
  pelago: { color: '#4a86b8', albedo: 0.30, air: true },
  bruma: { color: '#d8e2ec', albedo: 0.62, air: true },     // snow, bright
  viridis: { color: '#5e9e6e', albedo: 0.26, air: true },
  murex: { color: '#6a5690', albedo: 0.18, air: true },
  sonora: { color: '#c0b4d8', albedo: 0.36, air: false },   // a hollow, airless world
  ochrus: { color: '#c8945a', albedo: 0.24, air: true },
  tephra: { color: '#5a504c', albedo: 0.10, air: true },
  pallor: { color: '#d6ccb0', albedo: 0.42, air: true },
  gasGiant: { color: '#d8b890', albedo: 0.50, air: true },
  iceGiant: { color: '#6fa8c8', albedo: 0.42, air: true },
};
/** The gas giants' palettes, by the body's id (the generator does not colour): cream, tan, rose, pale blue. */
const GIANT_PALETTE = ['#d8b890', '#c8a070', '#d0a898', '#b8c8d8', '#e0c8a0'];

/** A body's diameter on screen (px) from which the sphere tier replaces the impostor. */
export const SPHERE_PX = 48;
/** The dot's size (px of radius) a body never shrinks under: a bright star's look. */
export const DOT_PX = 2.2;
/** The horizon a viewer on the ground hides bodies under (rad of elevation; a little under 0 for the ground's dips). */
export const GROUND_HORIZON = -0.02;
/**
 * 74.8: the horizon seen from `distance` out of a planet of `radius` — on the ground it is the ground's; from orbit it dips
 * below the viewer's level by acos(R / d) (at 2.6 radii, 67°), so a body stands in the sky until the planet's own disc
 * covers it (the depth test does the covering; this rule only stops the fade). The same for the sun's quad.
 */
export function horizonDip(radius: number, distance: number): number {
  if (!(distance > radius)) return GROUND_HORIZON;
  return Math.min(GROUND_HORIZON, -Math.acos(Math.min(1, radius / distance)));
}

/**
 * 74.6 (DECISIONS 348): the big bodies' surface — the sheet's looks (design_sheets/big_bodies.html). `today` is 74.1's
 * (a giant's bands as sines of the quad's y: the owner's "corduroy", and screen-aligned); the others draw the surface on the
 * sphere, by the body's own pole (the ring's normal, or an obliquity by id) — a giant's belts and zones of varied widths with
 * soft edges, turbulence and storms by the look; a rocky world's seas and land, caps, cloud, mare and craters, dunes by its
 * surface class. The look is compiled in (`BIG_LOOK`): an unchosen look costs nothing. Under `DETAIL_PX` of radius the
 * surface fades to the flat colour, so the small far bodies stay as 74.1 drew them.
 */
export type BigBodyLook = 'today' | 'zonal' | 'turbulent' | 'painted';
export const BIG_BODY_LOOKS: Record<BigBodyLook, { index: number; name: string; blurb: string }> = {
  today: { index: 0, name: 'today (74.1)', blurb: 'a giant: two sines of the screen\'s y, regular, screen-aligned; a rocky world: its flat colour and an air rim' },
  zonal: { index: 1, name: 'A zonal', blurb: 'on the sphere, by latitude: belts and zones of varied widths with soft edges and a polar hood; a rocky world mottled, caps at the poles' },
  turbulent: { index: 2, name: 'B turbulent', blurb: 'A plus the belt edges wandering with longitude (3D value noise), mottling within the belts, one or two storm ovals; a rocky world mapped: seas and land, caps, cloud; mare and craters; dunes' },
  painted: { index: 3, name: 'C painted', blurb: 'B with more contrast: cream zones, rust belts, dark lanes at the edges, a deeper hood; a rocky world\'s cloud in bands, sharper coasts, the sun\'s glint on the sea' },
};
/** The look the game draws — DECISIONS 349: the owner picked C painted ("turbulent or painted, whichever one is cheaper or painted if it's a rounding error"; C is ~120 ops a fragment over B on a giant, ~30 on a rocky world). */
export const DEFAULT_BIG_BODY_LOOK: BigBodyLook = 'painted';
/** The radius (px) over which the surface detail is fully drawn; under a quarter of it the body is its flat colour (74.1's small far bodies). */
export const DETAIL_PX = 14;
/** A body's surface class for the shader: 0 ocean world (land in seas, cloud), 1 gas giant, 2 ice giant, 3 icy world (frost, caps), 4 airless (mare, craters), 5 dry world (dunes, highland, thin cloud). */
export function surfaceClassOf(b: SystemBody): number {
  if (b.kind === 'gasGiant') return 1;
  if (b.kind === 'iceGiant') return 2;
  switch (b.archetype) {
    case 'thalia': case 'pelago': case 'viridis': return 0;
    case 'bruma': return 3;
    case 'sonora': return 4;
    default: return 5;
  }
}
/** A body's obliquity (rad) for the surface's pole when it has no ring: by id, within ±24°; deterministic, rendering only. */
export function obliquityOf(b: SystemBody): number { return b.ring ? b.ring.tilt : (((b.id * 7) % 25) - 12) * 2 * (Math.PI / 180); }

/**
 * 347: the ring's plain math as the body shader does it, in the impostor's basis (right, up, toward the viewer) with the
 * disc's radius 1 — the tests' copy (tests/systemSkyRing.test.ts); the shader and this are kept the same. Shadows are soft
 * over `SHADOW_SOFT` of a radius.
 */
export const SHADOW_SOFT = 0.04;
export const RING_MATH = {
  /** the ring plane's depth toward the viewer over the quad point `q`, for the ring's normal `n` (far behind when edge-on) */
  depth(q: readonly [number, number], n: Vec3): number { return Math.abs(n.z) > 1e-3 ? -(n.x * q[0] + n.y * q[1]) / n.z : -1e9; },
  /** 1 where the ring at `q` stands in front of the sphere's near face (or beyond the disc), 0 where the disc hides it */
  front(q: readonly [number, number], n: Vec3): number {
    const rr = q[0] * q[0] + q[1] * q[1];
    if (rr >= 1) return 1;
    return RING_MATH.depth(q, n) >= Math.sqrt(1 - rr) ? 1 : 0;
  },
  /** the ring colour's weight over what is drawn under it: pure beyond the disc, the ring's opacity over the disc where in front, none behind */
  weight(inDisc: number, ringA: number, front: number): number { return (1 + (ringA - 1) * inDisc) * front; },
  /** the ring's shadow on the sphere's surface point `p` (unit): the ray toward the sun crosses the ring's plane inside the ring (1 shadowed, 0 lit) */
  shadowOnSphere(p: Vec3, sun: Vec3, n: Vec3, inner: number, outer: number): number {
    const sN = sun.x * n.x + sun.y * n.y + sun.z * n.z, pN = p.x * n.x + p.y * n.y + p.z * n.z;
    if (Math.abs(sN) <= 1e-3) return 0;
    const t = -pN / sN;
    if (t < 0) return 0;
    const hr = Math.sqrt((p.x + sun.x * t) ** 2 + (p.y + sun.y * t) ** 2 + (p.z + sun.z * t) ** 2);
    return smooth(inner - SHADOW_SOFT, inner + SHADOW_SOFT, hr) * (1 - smooth(outer - SHADOW_SOFT, outer + SHADOW_SOFT, hr));
  },
  /** the sphere's shadow on the ring point `r`: the ray toward the sun passes through the sphere (1 in the shadow, 0 lit) */
  shadowOnRing(r: Vec3, sun: Vec3): number {
    const t = -(r.x * sun.x + r.y * sun.y + r.z * sun.z);
    if (t <= 0) return 0;
    const d = Math.sqrt((r.x + sun.x * t) ** 2 + (r.y + sun.y * t) ** 2 + (r.z + sun.z * t) ** 2);
    return 1 - smooth(1 - SHADOW_SOFT, 1 + SHADOW_SOFT, d);
  },
};

/** What the layer needs from the host each frame. */
export interface SystemSkyFrame {
  camera: THREE.PerspectiveCamera;
  /** the viewport's height in device pixels */
  heightPx: number;
  /** the eye (world) and the turn from the system's frame (core/systemGen) to the world */
  eye: THREE.Vector3;
  frame: THREE.Quaternion;
  /** how far out (world units) the bodies are drawn: inside the camera's far plane, beyond anything on the ground */
  distance: number;
  /** the local up (world unit) at the viewer — the horizon */
  up: THREE.Vector3;
  /** the sky from the viewer's body (core/systemGen skyFrom) */
  sky: SkyFromBody;
  /** the day: 0 night, 1 full day (skyHaze daylight); the air's thickness (core/air.ts: the sky's strength; 0 airless); the horizon's colour by day */
  daylight: number;
  air: number;
  skyColor: THREE.Color;
  /** 74.10 step 2: the sun's transmission through the air at the viewer (skyHaze sunTint; white when absent) — the disc and its glare take it */
  sunTint?: THREE.Color;
}

export interface BodyVisibility { alpha: number; haze: number; px: number; elevation: number }

/** A body's angular extent above its centre (rad): its drawn radius, or its ring's outer edge for a ringed giant — the horizon rule hides it only once this is under. */
export function bodyExtent(v: SkyView): number { return v.drawnRadius * (v.body?.ring ? Math.max(1, v.body.ring.outer) : 1); }

/** How much a body shows (0–1), its haze toward the sky, its drawn radius in px — the file's daylight rule. */
export function visibility(v: SkyView, sunView: SkyView, up: Vec3, daylight: number, air: number, heightPx: number, fovRad: number, floor: number = NIGHT_FLOOR.proposed, horizon: number = GROUND_HORIZON): BodyVisibility {
  const look = lookOf(v.body!);
  const elevation = Math.asin(Math.max(-1, Math.min(1, v.dir.x * up.x + v.dir.y * up.y + v.dir.z * up.z)));
  const px = (v.drawnRadius / fovRad) * heightPx;   // (small angles)
  // the lit face: albedo × flux; the sky by day: the air's thickness (an airless sky is black); a floor for the night's glow
  const S = look.albedo * v.flux;
  const skyB = skyBrightness(daylight, air, floor);   // (74.7: one rule with the stars' — core/starField.ts)
  // the phase: the share of the face the viewer sees lit (the sun's direction from the body against the viewer's)
  const lit = 0.5 * (1 + (v.sunDir.x * -v.dir.x + v.sunDir.y * -v.dir.y + v.sunDir.z * -v.dir.z));
  let c: number;
  if (px >= 1.5) c = S / skyB;                                                     // a resolved disc: surface brightness against the sky's
  else { const arcmin = v.trueRadius / (Math.PI / 180 / 60); c = (S * arcmin * arcmin * Math.max(lit, 0.05) * 8) / skyB; }   // a dot: its whole flux
  let alpha = smooth(0.6, 3, c) * smooth(0, 0.12, lit);
  // the sun's glare, broad by day in air
  const toSun = Math.acos(Math.max(-1, Math.min(1, v.dir.x * sunView.dir.x + v.dir.y * sunView.dir.y + v.dir.z * sunView.dir.z)));
  alpha *= smooth(1.5 * sunView.drawnRadius, 6 * sunView.drawnRadius + 0.05 * daylight * air, toSun);
  if (elevation + bodyExtent(v) < horizon) alpha = 0;   // under the horizon — the ground's, or from orbit the dipped one (74.8: `horizonDip`) — by its limb (rings included), not its centre: the ground (or the planet's disc, with the layers drawn past it) covers the part below (the owner: "pops in at the 50 % mark")
  const haze = Math.min(0.85, daylight * air * (0.2 + 0.6 * (1 - Math.max(0, Math.sin(elevation)))));
  return { alpha, haze, px, elevation };
}
const smooth = (a: number, b: number, x: number): number => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** The look of a body: by archetype, or a giant's. */
export function lookOf(b: SystemBody): { color: string; albedo: number; air: boolean } {
  if (b.kind === 'gasGiant') return { ...SKY_BODY_LOOKS.gasGiant, color: GIANT_PALETTE[b.id % GIANT_PALETTE.length] };
  if (b.kind === 'iceGiant') return SKY_BODY_LOOKS.iceGiant;
  return SKY_BODY_LOOKS[b.archetype ?? 'pallor'];
}

interface Slot { body: SystemBody; tier: 'impostor' | 'dot' | 'sphere' | 'hidden'; px: number }

export class SystemSkyLayer {
  readonly group = new THREE.Group();
  /** draw a body over SPHERE_PX as a low-detail sphere instead of its impostor */
  spheres = false;
  /** the big bodies' surface look this layer was compiled with (74.6) */
  readonly look: BigBodyLook;
  /** 74.8: the horizon this frame (rad of elevation): the ground's, or the dipped one from orbit (`horizonDip`) */
  horizon: number = GROUND_HORIZON;
  /** 74.7: the night floor of the sky's brightness the bodies fade against — DECISIONS 352: the dark sky (NIGHT_FLOOR.proposed) */
  nightFloor: number = NIGHT_FLOOR.proposed;
  private readonly max: number;
  private readonly bodies: THREE.InstancedMesh;
  private readonly bodyMat: THREE.ShaderMaterial;
  private readonly sun: THREE.Mesh;
  private readonly sunMat: THREE.ShaderMaterial;
  private readonly iDir: THREE.InstancedBufferAttribute; private readonly iRadius: THREE.InstancedBufferAttribute; private readonly iColor: THREE.InstancedBufferAttribute;
  private readonly iSun: THREE.InstancedBufferAttribute; private readonly iParams: THREE.InstancedBufferAttribute; private readonly iRingN: THREE.InstancedBufferAttribute; private readonly iMisc: THREE.InstancedBufferAttribute;
  private readonly spherePool: THREE.Mesh[] = [];
  private readonly sphereGeo = new THREE.IcosahedronGeometry(1, 3);   // (three's detail n: 20 × (n + 1)² faces — 320)
  private slots: Slot[] = [];

  constructor(max = 48, look: BigBodyLook = DEFAULT_BIG_BODY_LOOK) {
    this.max = max;
    this.look = look;
    const geo = new THREE.PlaneGeometry(2, 2);   // (an InstancedMesh draws `count` instances of it; the instanced attributes ride on the geometry)
    const mk = (n: number) => new THREE.InstancedBufferAttribute(new Float32Array(max * n), n);
    geo.setAttribute('iDir', this.iDir = mk(3)); geo.setAttribute('iRadius', this.iRadius = mk(1)); geo.setAttribute('iColor', this.iColor = mk(3));
    geo.setAttribute('iSun', this.iSun = mk(3)); geo.setAttribute('iParams', this.iParams = mk(4)); geo.setAttribute('iRingN', this.iRingN = mk(3)); geo.setAttribute('iMisc', this.iMisc = mk(4));
    this.bodyMat = new THREE.ShaderMaterial({
      uniforms: { uEye: { value: new THREE.Vector3() }, uDistance: { value: 100 }, uRight: { value: new THREE.Vector3(1, 0, 0) }, uUp: { value: new THREE.Vector3(0, 1, 0) }, uHeightPx: { value: 800 }, uTanHalf: { value: 0.5 }, uMinPx: { value: DOT_PX }, uSky: { value: new THREE.Color() } },
      defines: { BIG_LOOK: BIG_BODY_LOOKS[look].index },
      transparent: true, depthWrite: false, depthTest: true, side: THREE.DoubleSide,
      vertexShader: /* glsl */`
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute vec3 iDir; attribute float iRadius; attribute vec3 iColor; attribute vec3 iSun; attribute vec4 iParams; attribute vec3 iRingN; attribute vec4 iMisc;
        uniform vec3 uEye, uRight, uUp; uniform float uDistance, uHeightPx, uTanHalf, uMinPx;
        varying vec2 vUv; varying float vDisc, vPx; flat varying vec3 vDir, vSun, vColor, vRingN; flat varying vec4 vParams, vMisc;
        void main() {
          vec3 centre = uEye + iDir * uDistance;
          float discHalf = uDistance * tan(iRadius);
          float pxPerUnit = uHeightPx / (2.0 * uTanHalf * uDistance);
          float quadHalf = max(discHalf * max(1.0, iParams.w), uMinPx / pxPerUnit);
          vDisc = discHalf / quadHalf; vPx = discHalf * pxPerUnit;
          vUv = position.xy; vDir = iDir; vSun = iSun; vColor = iColor; vRingN = iRingN; vParams = iParams; vMisc = iMisc;
          vec3 world = centre + uRight * (position.x * quadHalf) + uUp * (position.y * quadHalf);
          gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
          if (iMisc.x <= 0.001) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
          #include <logdepthbuf_vertex>
        }`,
      fragmentShader: /* glsl */`
        #include <common>
        #include <logdepthbuf_pars_fragment>
        uniform vec3 uRight, uUp, uSky;
        varying vec2 vUv; varying float vDisc, vPx; flat varying vec3 vDir, vSun, vColor, vRingN; flat varying vec4 vParams, vMisc;
        #if BIG_LOOK > 0
        // 74.6: the surface on the sphere. 3D value noise over the surface normal (seamless; the body is static, so the world
        // normal is the body's own), two or three octaves; a giant's bands by latitude about its pole.
        float hash3(vec3 p) { p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3)); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
        float vnoise(vec3 p) {
          vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(mix(hash3(i), hash3(i + vec3(1.0, 0.0, 0.0)), f.x), mix(hash3(i + vec3(0.0, 1.0, 0.0)), hash3(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
                     mix(mix(hash3(i + vec3(0.0, 0.0, 1.0)), hash3(i + vec3(1.0, 0.0, 1.0)), f.x), mix(hash3(i + vec3(0.0, 1.0, 1.0)), hash3(i + vec3(1.0, 1.0, 1.0)), f.x), f.y), f.z);
        }
        float fbm2(vec3 p) { return 0.65 * vnoise(p) + 0.35 * vnoise(p * 2.03 + 7.1); }
        float fbm3(vec3 p) { return 0.55 * vnoise(p) + 0.3 * vnoise(p * 2.03 + 7.1) + 0.15 * vnoise(p * 4.1 + 3.3); }
        /** a giant's surface: belts and zones about its pole, varied, by the look */
        vec3 giant(vec3 base, vec3 n, float cls, float seed) {
          vec3 axis = normalize(vRingN);
          float lat = dot(n, axis);
          float bands = vParams.y > 0.5 ? vParams.y : 3.0;   // (an ice giant: a few faint bands)
          float warp = 0.35 * sin(lat * 2.3 + seed) + 0.25 * sin(lat * 5.1 + seed * 1.7);   // widths vary along the latitude
          float sc = lat * bands * 3.14159 + warp * bands * 0.6;
          float amp = cls > 1.5 ? 0.45 : 1.0;
          #if BIG_LOOK >= 2
          vec3 ref = abs(dot(axis, vDir)) < 0.9 ? vDir : vec3(0.0, 0.0, 1.0);
          vec3 e1 = normalize(cross(axis, ref));
          vec3 e2 = cross(axis, e1);
          float lon = atan(dot(n, e2), dot(n, e1));
          #if BIG_LOOK == 3
          float tur = fbm3(n * 4.0 + seed * 3.0);
          #else
          float tur = fbm2(n * 4.0 + seed * 3.0);
          #endif
          sc += (tur - 0.5) * 2.2 * amp;   // the belt edges wander with longitude
          float mottle = 0.88 + 0.24 * vnoise(n * 16.0 + seed * 5.0);
          #endif
          float b = sin(sc);
          float belt = smoothstep(-0.55, 0.55, b);
          #if BIG_LOOK == 3
          vec3 zone = mix(base, vec3(1.0, 0.97, 0.9), 0.3) * 1.04;
          vec3 beltC = base * vec3(0.74, 0.56, 0.44);
          #else
          vec3 zone = base * 1.05;
          vec3 beltC = base * vec3(0.80, 0.68, 0.58);
          #endif
          if (cls > 1.5) { zone = base * 1.03; beltC = base * 0.84; }
          vec3 col = mix(zone, beltC, belt * amp);
          #if BIG_LOOK >= 2
          col *= mix(1.0, mottle, 0.3 + 0.7 * belt);
          // a storm oval: at a latitude by the body, a longitude by the body — rust on a gas giant, a dark spot on an ice giant; a second, smaller, on the other side
          for (int k = 0; k < 2; k++) {
            float fk = float(k);
            float latS = (0.22 + 0.35 * fract(seed * 0.73 + fk * 0.41)) * (fract(seed * 1.37 + fk * 0.5) > 0.5 ? 1.0 : -1.0);
            float lonS = seed * 2.1 + fk * 2.6;
            float dLon = atan(sin(lon - lonS), cos(lon - lonS));
            float size = k == 0 ? 1.0 : 0.6;
            float d = length(vec2(dLon * sqrt(max(0.0, 1.0 - latS * latS)) / (0.24 * size), (lat - latS) / (0.085 * size)));
            float inS = 1.0 - smoothstep(0.75, 1.05, d);
            float rim = smoothstep(0.6, 0.9, d) * (1.0 - smoothstep(1.0, 1.3, d));
            vec3 stormC = cls > 1.5 ? base * 0.62 : (k == 0 ? base * vec3(0.98, 0.62, 0.48) : mix(base, vec3(1.0), 0.45));
            col = mix(col, stormC, inS * amp);
            col *= 1.0 + 0.12 * rim * amp;
          }
          #endif
          #if BIG_LOOK == 3
          float lane = 1.0 - smoothstep(0.0, 0.22, abs(b));   // a dark lane where a belt meets a zone
          col *= 1.0 - 0.16 * lane * amp;
          #endif
          float hood = smoothstep(0.72, 0.95, abs(lat));
          #if BIG_LOOK == 3
          col = mix(col, base * vec3(0.66, 0.62, 0.62), hood * 0.7);
          #else
          col = mix(col, base * vec3(0.76, 0.72, 0.72), hood * 0.55);
          #endif
          return col;
        }
        /** a rocky world's surface by its class: mottled (A), mapped (B), mapped with banded cloud and a sea glint (C) */
        vec3 rocky(vec3 base, vec3 n, float cls, float seed, vec3 toViewer) {
          vec3 axis = normalize(vRingN);
          float lat = abs(dot(n, axis));
          vec3 col = base;
          #if BIG_LOOK == 1
          float m = fbm2(n * 3.0 + seed * 3.0);
          col = base * (0.8 + 0.4 * m);
          if (cls > 3.5 && cls < 4.5) col = mix(col, base * 0.6, smoothstep(0.55, 0.62, m));   // mare
          if (cls < 0.5 || cls > 4.5) col = mix(col, vec3(0.94, 0.95, 0.97), smoothstep(0.91, 0.96, lat + 0.03 * (m - 0.5)));   // caps (lat is sin(latitude): from ~66°)
          if (cls > 2.5 && cls < 3.5) col = mix(col, vec3(0.93, 0.95, 0.98), 0.5 * smoothstep(0.4, 0.6, m));   // frost
          #else
          float m = fbm3(n * 2.5 + seed * 3.0);
          float fine = vnoise(n * 14.0 + seed * 5.0);
          if (cls < 0.5) {
            // an ocean world: seas, land in them, caps
            float land = smoothstep(0.50, 0.56, m);
            vec3 sea = base * 0.78;
            vec3 ground = mix(base, vec3(0.50, 0.46, 0.32), 0.72) * (0.82 + 0.36 * fine);
            #if BIG_LOOK == 3
            land = smoothstep(0.515, 0.545, m);
            sea = base * 0.7;
            #endif
            col = mix(sea, ground, land);
            col = mix(col, vec3(0.94, 0.95, 0.97), smoothstep(0.90, 0.96, lat + 0.04 * (m - 0.5)));   // caps from ~64° (sin)
            #if BIG_LOOK == 3
            float glint = pow(max(dot(reflect(-vSun, n), toViewer), 0.0), 60.0);
            col += vec3(0.9, 0.85, 0.7) * glint * (1.0 - land) * 0.6;
            #endif
          } else if (cls < 3.5) {
            // an icy world: frost plains and darker ground, cracks
            col = mix(base * 0.82, vec3(0.93, 0.95, 0.98), smoothstep(0.38, 0.62, m));
            col *= 1.0 - 0.18 * (1.0 - smoothstep(0.0, 0.05, abs(fine - 0.5)));
          } else if (cls < 4.5) {
            // an airless world: mare, a crater field (dark pits with bright rims)
            col = mix(base, base * 0.6, smoothstep(0.52, 0.6, m));
            float c = vnoise(n * 22.0 + seed * 5.0);
            col *= 1.0 - 0.3 * smoothstep(0.8, 0.9, c) + 0.14 * smoothstep(0.7, 0.78, c) * (1.0 - smoothstep(0.8, 0.86, c));
            col *= 0.9 + 0.2 * fine;
          } else {
            // a dry world: dunes, dark highland, caps where the poles are cold
            col = base * (0.85 + 0.3 * fine);
            col = mix(col, base * 0.68, smoothstep(0.55, 0.66, m));
            col = mix(col, vec3(0.94, 0.93, 0.92), smoothstep(0.94, 0.98, lat + 0.03 * (m - 0.5)));   // a small cap, from ~70°
          }
          // cloud over the air worlds: white patches, banded in C
          if (vParams.x > 0.5 && (cls < 0.5 || cls > 4.5 || (cls > 2.5 && cls < 3.5))) {
            float cl = fbm3(n * 3.5 + seed * 3.0 + 31.0);
            #if BIG_LOOK == 3
            cl += 0.09 * sin(dot(n, axis) * 11.0 + seed);
            float cloud = smoothstep(0.52, 0.7, cl);
            #else
            float cloud = smoothstep(0.54, 0.72, cl);
            #endif
            vec3 cloudC = vec3(0.96, 0.97, 0.98);
            if (cls > 4.5) { cloud *= 0.55; cloudC = mix(base, cloudC, 0.6); }   // a dry world's thin wisps, dust-tinted
            col = mix(col, cloudC, cloud * 0.92);
          }
          #endif
          return col;
        }
        #endif
        void main() {
          #include <logdepthbuf_fragment>
          float r = length(vUv), aa = fwidth(r);
          vec3 col = vec3(0.0); float alpha = 0.0;
          vec3 toViewer = -vDir;
          if (vPx < 1.5) {
            // a dot: the body's lit colour as a soft point
            float lit = 0.5 + 0.5 * dot(vSun, toViewer);
            col = vColor * (0.6 + 0.4 * lit) + vec3(0.45) * smoothstep(0.6, 0.0, r);   // a bright core, the body's tint round it
            alpha = smoothstep(1.0, 0.2, r);
          } else {
            vec2 q = vUv / vDisc; float rr = dot(q, q);
            float inDisc = 1.0 - smoothstep(vDisc - aa, vDisc + aa, r);
            float nz = sqrt(max(0.0, 1.0 - rr));
            // the impostor's basis: the camera's right and up squared to the view ray, so the surface point is a unit normal
            vec3 R = normalize(uRight - toViewer * dot(uRight, toViewer)), U = cross(toViewer, R);
            vec3 n = R * q.x + U * q.y + toViewer * nz;
            float l = dot(n, vSun);
            float lit = smoothstep(-0.08, 0.22, l);
            // a sphere, not a cutout (347): Lambert over the lit face from the soft terminator, the limb darkened (a giant's most)
            float shade = lit * (0.25 + 0.75 * max(l, 0.0));
            shade *= 1.0 - (vParams.y > 0.5 ? 0.5 : 0.3) * pow(1.0 - nz, 1.6);
            vec3 base = vColor;
            #if BIG_LOOK == 0
            if (vParams.y > 0.5) base *= 1.0 + 0.09 * sin(q.y * vParams.y * 3.14159 + 0.6 * sin(q.x * 2.5)) + 0.04 * sin(q.y * vParams.y * 7.1);   // a giant's bands
            #else
            // 74.6: the surface on the sphere, faded in as the disc grows (the small far bodies keep their flat colour)
            float detail = smoothstep(${(DETAIL_PX / 4).toFixed(1)}, ${DETAIL_PX.toFixed(1)}, vPx);
            if (detail > 0.0) base = mix(base, vMisc.z > 0.5 && vMisc.z < 2.5 ? giant(base, n, vMisc.z, vMisc.w * 6.2832) : rocky(base, n, vMisc.z, vMisc.w * 6.2832, toViewer), detail);
            #endif
            float nx = 0.0, ny = 0.0, nzr = 0.0;
            if (vParams.w > 1.0) {
              nx = dot(vRingN, R); ny = dot(vRingN, U); nzr = dot(vRingN, toViewer);
              // the ring's shadow on the face: from the surface point toward the sun, the ray crosses the ring's plane inside the ring (RING_MATH.shadowOnSphere)
              float sN = dot(vSun, vRingN), pN = dot(n, vRingN);
              if (abs(sN) > 1e-3) {
                float t = -pN / sN;
                float hr = length(n + vSun * t), w = ${SHADOW_SOFT};
                float inRing = smoothstep(vParams.z - w, vParams.z + w, hr) * (1.0 - smoothstep(vParams.w - w, vParams.w + w, hr));
                shade *= 1.0 - 0.7 * inRing * step(0.0, t);
              }
            }
            vec3 disc = base * (0.012 + 0.988 * shade);
            if (vParams.x > 0.5) disc += mix(vec3(0.55, 0.7, 1.0), base, 0.4) * pow(1.0 - nz, 2.6) * 0.55 * max(lit, 0.08);   // an air world's rim
            col = disc; alpha = inDisc;
            if (vParams.w > 1.0) {
              // the ring: a tilted ellipse round the disc, drawn over the disc on its near side and hidden behind it on the far (RING_MATH.front, .weight)
              vec2 minorDir = length(vec2(nx, ny)) > 1e-3 ? normalize(vec2(nx, ny)) : vec2(1.0, 0.0);
              vec2 majorDir = vec2(-minorDir.y, minorDir.x);
              float ratio = max(abs(nzr), 0.05);
              float e = sqrt(pow(dot(q, majorDir), 2.0) + pow(dot(q, minorDir) / ratio, 2.0));
              float eaa = fwidth(e) * 1.2;
              float band = smoothstep(vParams.z - eaa, vParams.z + eaa, e) * (1.0 - smoothstep(vParams.w - eaa, vParams.w + eaa, e));
              band *= 0.55 + 0.45 * smoothstep(0.0, 0.08, abs(sin((e - vParams.z) / (vParams.w - vParams.z) * 9.0)));   // gaps
              float zRing = abs(nzr) > 1e-3 ? -(nx * q.x + ny * q.y) / nzr : -1e9;   // the ring point's depth toward the viewer
              float front = rr < 1.0 ? step(nz, zRing) : 1.0;
              // the body's shadow on the ring: the ring point's ray toward the sun passes through the body (RING_MATH.shadowOnRing)
              vec3 rp = R * q.x + U * q.y + toViewer * zRing;
              float ts = -dot(rp, vSun);
              float dark = ts > 0.0 ? 1.0 - smoothstep(1.0 - ${SHADOW_SOFT}, 1.0 + ${SHADOW_SOFT}, length(rp + vSun * ts)) : 0.0;
              float ringLit = (0.25 + 0.75 * abs(dot(vRingN, vSun)) * smoothstep(-0.3, 0.3, dot(toViewer, vSun) + 0.6)) * (1.0 - 0.85 * dark);
              vec3 ringCol = mix(vColor, vec3(1.0), 0.35) * ringLit;
              float ringA = band * 0.8;
              float w = mix(1.0, ringA, inDisc) * front;   // the ring's colour: pure beyond the disc, over the disc in front, none behind
              col = mix(col, ringCol, w);
              alpha = max(alpha, ringA * front);
            }
          }
          col = mix(col, uSky, vMisc.y);   // hazed toward the sky by day, the lower it stands
          gl_FragColor = vec4(col, alpha * vMisc.x);
          #include <colorspace_fragment>
        }`,
    });
    this.bodies = new THREE.InstancedMesh(geo, this.bodyMat, max);
    this.bodies.name = 'systemSky.bodies';   // (every instanced layer named: tests/instancedNames.test.ts)
    this.bodies.frustumCulled = false;
    this.bodies.renderOrder = 1;   // after the sky dome (-1), before what stands on the ground
    this.group.add(this.bodies);
    // the sun: a disc and its glare, additive
    this.sunMat = new THREE.ShaderMaterial({
      uniforms: { uCore: { value: new THREE.Color('#fff4e0') }, uCorona: { value: new THREE.Color('#ffaa44') }, uDisc: { value: 0.3 }, uGlare: { value: 1 }, uDay: { value: 1 }, uTint: { value: new THREE.Color(1, 1, 1) } },
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      vertexShader: /* glsl */`
        #include <common>
        #include <logdepthbuf_pars_vertex>
        varying vec2 vUv;
        void main() {
          vUv = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          #include <logdepthbuf_vertex>
        }`,
      fragmentShader: /* glsl */`
        #include <common>
        #include <logdepthbuf_pars_fragment>
        uniform vec3 uCore, uCorona, uTint; uniform float uDisc, uGlare, uDay; varying vec2 vUv;
        void main() {
          #include <logdepthbuf_fragment>
          float r = length(vUv), aa = fwidth(r);
          float disc = 1.0 - smoothstep(uDisc - aa, uDisc + aa, r);
          float glare = pow(max(0.0, 1.0 - r), 2.4) * uGlare;
          vec3 core = mix(uCorona, uCore, 0.35 + 0.65 * uDay);
          gl_FragColor = vec4((core * disc * 1.4 + uCorona * glare) * uTint, 1.0);   // (74.10 step 2: red and dim through a low sun's air)
          #include <colorspace_fragment>
        }`,
    });
    this.sun = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.sunMat);
    this.sun.frustumCulled = false; this.sun.renderOrder = 1;
    this.group.add(this.sun);
  }

  /** Place, size, light and fade every body and the sun for this frame. */
  update(f: SystemSkyFrame): void {
    const u = this.bodyMat.uniforms;
    f.camera.updateMatrixWorld();
    const right = V3[0].set(1, 0, 0).applyQuaternion(f.camera.quaternion), camUp = V3[1].set(0, 1, 0).applyQuaternion(f.camera.quaternion);
    u.uEye.value.copy(f.eye); u.uDistance.value = f.distance; u.uRight.value.copy(right); u.uUp.value.copy(camUp);
    u.uHeightPx.value = f.heightPx; u.uTanHalf.value = Math.tan((f.camera.fov * Math.PI) / 360); u.uSky.value.copy(f.skyColor);
    const fov = (f.camera.fov * Math.PI) / 180;
    const upSys = V3[2].copy(f.up).applyQuaternion(Q.copy(f.frame).invert());   // the local up in the system frame
    const upS = { x: upSys.x, y: upSys.y, z: upSys.z };
    let k = 0, sphereK = 0;
    this.slots = [];
    for (const v of f.sky.bodies) {
      if (k >= this.max) break;
      const vis = visibility(v, f.sky.sun, upS, f.daylight, f.air, f.heightPx, fov, this.nightFloor, this.horizon);
      const look = lookOf(v.body!), b = v.body!;
      const dirW = V3[3].set(v.dir.x, v.dir.y, v.dir.z).applyQuaternion(f.frame);
      const sunW = V3[4].set(v.sunDir.x, v.sunDir.y, v.sunDir.z).applyQuaternion(f.frame);
      const useSphere = this.spheres && vis.alpha > 0 && vis.px * 2 >= SPHERE_PX;
      const slot: Slot = { body: b, tier: vis.alpha <= 0.001 ? 'hidden' : useSphere ? 'sphere' : vis.px < 1.5 ? 'dot' : 'impostor', px: vis.px };
      this.slots.push(slot);
      this.iDir.setXYZ(k, dirW.x, dirW.y, dirW.z); this.iRadius.setX(k, v.drawnRadius);
      C.set(look.color); this.iColor.setXYZ(k, C.r, C.g, C.b);
      this.iSun.setXYZ(k, sunW.x, sunW.y, sunW.z);
      this.iParams.setXYZW(k, look.air ? 1 : 0, b.bands, b.ring?.inner ?? 0, b.ring?.outer ?? 0);
      // the body's pole (the ring's normal, or an obliquity by id): the system's plane tilted about the body's direction's perpendicular
      const n = V3[5].set(0, 1, 0).applyAxisAngle(V3[6].set(1, 0, 0), obliquityOf(b)).applyAxisAngle(V3[6].set(0, 1, 0), b.angle).applyQuaternion(f.frame);
      this.iRingN.setXYZ(k, n.x, n.y, n.z);
      this.iMisc.setXYZW(k, useSphere ? 0 : vis.alpha, vis.haze, surfaceClassOf(b), (b.id * 0.6180339887) % 1);   // 74.6: the surface class and the body's seed
      if (useSphere) {
        const m = this.sphere(sphereK++), scale = f.distance * Math.tan(v.drawnRadius);
        m.position.copy(dirW).multiplyScalar(f.distance).add(f.eye); m.scale.setScalar(scale);
        const mu = (m.material as THREE.ShaderMaterial).uniforms;
        mu.uColor.value.set(look.color); mu.uSun.value.copy(sunW); mu.uAlpha.value = vis.alpha; mu.uHaze.value = vis.haze; mu.uSky.value.copy(f.skyColor);
        m.visible = true;
      }
      k++;
    }
    for (let i = sphereK; i < this.spherePool.length; i++) this.spherePool[i].visible = false;
    this.bodies.count = k;
    for (const a of [this.iDir, this.iRadius, this.iColor, this.iSun, this.iParams, this.iRingN, this.iMisc]) a.needsUpdate = true;
    // the sun
    const s = f.sky.sun, sunDirW = V3[3].set(s.dir.x, s.dir.y, s.dir.z).applyQuaternion(f.frame);
    const glareR = s.drawnRadius * (f.air > 0 ? 2.5 + 6 * f.air * f.daylight : 2.2);
    const half = f.distance * Math.tan(glareR);
    this.sun.position.copy(sunDirW).multiplyScalar(f.distance).add(f.eye);
    this.sun.quaternion.copy(f.camera.quaternion); this.sun.scale.setScalar(half);
    const su = this.sunMat.uniforms;
    su.uDisc.value = s.drawnRadius / glareR; su.uGlare.value = f.air > 0 ? 0.6 + 0.5 * f.daylight : 0.5; su.uDay.value = f.daylight;
    if (f.sunTint) su.uTint.value.copy(f.sunTint); else su.uTint.value.setRGB(1, 1, 1);
    this.sun.visible = Math.asin(Math.max(-1, Math.min(1, sunDirW.dot(f.up)))) + glareR > this.horizon;   // (74.8: from orbit the sun shows past the limb; by its glare's edge, as the bodies by theirs)
  }

  /** The star's colours (STAR_TYPES core / corona). */
  setStar(core: string, corona: string): void { this.sunMat.uniforms.uCore.value.set(core); this.sunMat.uniforms.uCorona.value.set(corona); }

  private sphere(i: number): THREE.Mesh {
    while (this.spherePool.length <= i) {
      const m = new THREE.Mesh(this.sphereGeo, sphereMaterial());
      m.frustumCulled = false; m.renderOrder = 1;
      this.spherePool.push(m); this.group.add(m);
    }
    return this.spherePool[i];
  }

  /** This frame's tiers, draws and triangles. */
  stats(): { impostor: number; dot: number; sphere: number; hidden: number; draws: number; triangles: number } {
    const n = { impostor: 0, dot: 0, sphere: 0, hidden: 0, draws: 1 + (this.sun.visible ? 1 : 0), triangles: this.sun.visible ? 2 : 0 };
    for (const s of this.slots) {
      n[s.tier]++;
      if (s.tier === 'impostor' || s.tier === 'dot') n.triangles += 2;
      if (s.tier === 'sphere') { n.draws++; n.triangles += this.sphereGeo.index ? this.sphereGeo.index.count / 3 : this.sphereGeo.attributes.position.count / 3; }
    }
    return n;
  }
  /** Each body's tier and size this frame, for a sheet's table. */
  tiers(): readonly Slot[] { return this.slots; }

  dispose(): void {
    this.bodies.geometry.dispose(); this.bodyMat.dispose(); this.sun.geometry.dispose(); this.sunMat.dispose(); this.sphereGeo.dispose();
    for (const m of this.spherePool) (m.material as THREE.Material).dispose();
  }
}
/** The sphere tier's material: the body's colour shaded by the sun's direction at the body (not the scene's light), hazed and faded as the impostor is. */
function sphereMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color() }, uSun: { value: new THREE.Vector3(0, 1, 0) }, uAlpha: { value: 1 }, uHaze: { value: 0 }, uSky: { value: new THREE.Color() } },
    transparent: true, depthWrite: false,
    vertexShader: /* glsl */`
      varying vec3 vN;
      void main() { vN = normalize(mat3(modelMatrix) * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */`
      uniform vec3 uColor, uSun, uSky; uniform float uAlpha, uHaze; varying vec3 vN;
      void main() {
        float l = dot(normalize(vN), uSun), lit = smoothstep(-0.08, 0.22, l);
        vec3 col = mix(uColor * (0.012 + 0.988 * lit * (0.25 + 0.75 * max(l, 0.0))), uSky, uHaze);
        gl_FragColor = vec4(col, uAlpha);
        #include <colorspace_fragment>
      }`,
  });
}
const V3 = Array.from({ length: 7 }, () => new THREE.Vector3());
const Q = new THREE.Quaternion(), C = new THREE.Color();
